import type { AriaButtonProps } from "@react-types/button";
import type { Node } from "@react-types/shared";
import { IconCheckmark2Small } from "central-icons/IconCheckmark2Small";
import { IconChevronDownSmall } from "central-icons/IconChevronDownSmall";
import type React from "react";
import { Children, isValidElement, useRef, type FC } from "react";
import { DismissButton, HiddenSelect, useButton, useListBox, useOption, usePopover, useSelect } from "react-aria";
import type { AriaPopoverProps, AriaListBoxOptions } from "react-aria";
import { createPortal } from "react-dom";
import { Item, useSelectState } from "react-stately";
import type { OverlayTriggerState, ListState } from "react-stately";

import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { Text } from "@/components/flexkit/typography/Text";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import type { PropsWithTestId } from "@/components/podkit/lib/testid";

/**
 * Component displayName constants for Fast Refresh compatibility.
 */
const SELECT_ITEM = "Select.Item";
const SELECT_ITEM_ICON = "Select.Item.Icon";
const SELECT_ITEM_DESCRIPTION = "Select.Item.Description";
const SELECT_FOOTER = "Select.Footer";

/**
 * Props for the root Select component.
 */
export type SelectProps = PropsWithClassName &
    PropsWithTestId & {
        /** The default value for uncontrolled usage */
        defaultValue?: string;
        /** The current value for controlled usage */
        value?: string;
        /** Callback when value changes */
        onValueChange?: (value: string) => void;
        /** Whether the select is disabled */
        disabled?: boolean;
        /** Whether the select is in loading state */
        loading?: boolean;
        /** Placeholder text when no value is selected */
        placeholder?: string | React.ReactNode;
        /** Custom function to format the display value in the trigger */
        formatDisplayValue?: (value: string, label: React.ReactNode) => React.ReactNode;
        /** Whether the popover should match the trigger width. Defaults to true. */
        sameWidth?: boolean;
        /** The name of the hidden select element for form submission */
        name?: string;
        /** The id for the trigger button, enabling `<label htmlFor={id}>` to focus the select */
        id?: string;
        /** Accessible label for the select when no visible label is present */
        "aria-label"?: string;
    };

/**
 * Props for Select.Item component.
 */
export type SelectItemProps = {
    /** The value of this item */
    value: string;
    /** Item content */
    children?: React.ReactNode;
    /** Whether the item is disabled */
    disabled?: boolean;
    /** Additional CSS classes */
    className?: string;
};

/**
 * Helper function to get the displayName from a React element.
 */
const getComponentDisplayName = (element: React.ReactElement): string | undefined => {
    const type = element.type as React.ComponentType;
    return type.displayName;
};

/**
 * Styled placeholder text with muted color.
 */
const SelectPlaceholder: FC<React.ComponentProps<"span">> = ({ children, className, ...props }) => (
    <span className={cn("text-content-muted", className)} {...props}>
        {children}
    </span>
);
SelectPlaceholder.displayName = "Select.Placeholder";

/**
 * Footer slot for Select. Renders content at the bottom of the dropdown.
 * Uses negative margins to extend border to full width despite popover padding.
 */
const SelectFooter: FC<React.ComponentProps<"div">> = ({ children, className, ...props }) => (
    <div className={cn("-mx-1 mt-1 border-t border-border-base px-1 pt-1", className)} {...props}>
        {children}
    </div>
);
SelectFooter.displayName = SELECT_FOOTER;

/**
 * Icon slot for Select.Item. Renders an icon at the start of the item.
 */
const SelectItemIcon: FC<React.ComponentProps<"span">> = ({ children, className, ...props }) => {
    if (!children) return null;
    return (
        <span className={cn("flex-none", className)} {...props}>
            {children}
        </span>
    );
};
SelectItemIcon.displayName = SELECT_ITEM_ICON;

/**
 * Description slot for Select.Item. Renders secondary text below the main content.
 */
const SelectItemDescription: FC<React.ComponentProps<typeof Text>> = ({ children, className, ...props }) => {
    return (
        <Text className={cn("text-sm text-content-secondary", className)} {...props}>
            {children}
        </Text>
    );
};
SelectItemDescription.displayName = SELECT_ITEM_DESCRIPTION;

/**
 * Extracts only the label text from item children, excluding Icon and Description slots.
 */
const extractItemLabel = (children: React.ReactNode): React.ReactNode => {
    const labelParts: React.ReactNode[] = [];
    Children.forEach(children, (child) => {
        if (isValidElement(child)) {
            const displayName = getComponentDisplayName(child);
            if (displayName === SELECT_ITEM_ICON || displayName === SELECT_ITEM_DESCRIPTION) {
                return;
            }
        }
        labelParts.push(child);
    });
    return labelParts.length === 1 ? labelParts[0] : labelParts;
};

/**
 * Converts label content to a plain text string for textValue.
 */
const labelToTextValue = (children: React.ReactNode): string => {
    const label = extractItemLabel(children);
    if (typeof label === "string") return label;
    if (typeof label === "number") return String(label);
    // For arrays or complex nodes, flatten to string
    const parts: string[] = [];
    Children.forEach(label, (child) => {
        if (typeof child === "string" || typeof child === "number") {
            parts.push(String(child));
        }
    });
    return parts.join("") || "";
};

// ============================================================================
// Internal Popover
// ============================================================================

type SelectPopoverProps = Omit<AriaPopoverProps, "popoverRef" | "triggerRef"> & {
    children: React.ReactNode;
    state: OverlayTriggerState;
    triggerRef: React.RefObject<HTMLElement | null>;
    className?: string;
    /** Width of the trigger element in pixels */
    triggerWidth?: number;
    /** Whether the popover should exactly match the trigger width vs use it as a minimum */
    sameWidth?: boolean;
};

const SelectPopover: FC<SelectPopoverProps> = ({
    children,
    state,
    className,
    triggerWidth,
    sameWidth = false,
    ...props
}) => {
    const popoverRef = useRef<HTMLDivElement>(null);
    const { popoverProps } = usePopover(
        {
            ...props,
            popoverRef,
            // Non-modal so usePopover doesn't call usePreventScroll.
            // Our app scrolls on a nested container, not document.documentElement,
            // so usePreventScroll's overflow:hidden on <html> causes layout jumps.
            isNonModal: true,
        },
        state,
    );

    const widthStyle: React.CSSProperties = triggerWidth
        ? sameWidth
            ? { width: triggerWidth }
            : { minWidth: triggerWidth }
        : {};

    // isNonModal disables useOverlay's isDismissable, so we handle outside
    // clicks manually via the underlay. On dismiss we also restore focus to
    // the trigger — react-aria only does this automatically for modal overlays.
    const dismiss = () => {
        state.close();
        props.triggerRef.current?.focus();
    };

    // HACK: Radix dialog uses FocusScope (trapped) and aria-hidden's hideOthers().
    // Portal into the dialog content so the popover is inside the focus trap.
    // Overflow is handled by onOpenChange in the root Select component.
    // Remove this when Radix is replaced.
    const portalContainer = props.triggerRef.current?.closest("[role='dialog']") ?? document.body;

    return createPortal(
        <>
            <div
                style={{ position: "fixed", inset: 0, zIndex: 998, pointerEvents: "auto" }}
                onPointerDown={(e) => {
                    // Prevent Firefox text selection bug (from underlayProps)
                    if (e.target === e.currentTarget) e.preventDefault();
                    dismiss();
                }}
            />
            <div
                {...popoverProps}
                ref={popoverRef}
                className={className}
                style={{
                    ...popoverProps.style,
                    ...widthStyle,
                    pointerEvents: "auto",
                }}
            >
                <DismissButton onDismiss={dismiss} />
                {children}
                <DismissButton onDismiss={dismiss} />
            </div>
        </>,
        portalContainer,
    );
};

// ============================================================================
// Internal react-aria ListBox + Option
// ============================================================================

type SelectListBoxProps = {
    menuProps: AriaListBoxOptions<unknown>;
    state: ListState<unknown>;
};

const SelectListBox: FC<SelectListBoxProps> = ({ menuProps, state }) => {
    const listBoxRef = useRef<HTMLUListElement>(null);
    const { listBoxProps } = useListBox(menuProps, state, listBoxRef);

    return (
        <ul
            {...listBoxProps}
            ref={listBoxRef}
            className="m-0 list-none overflow-auto p-0"
            style={{ maxHeight: "inherit", outline: "none" }}
        >
            {[...state.collection].map((item) => (
                <SelectOptionItem key={item.key} item={item} state={state} />
            ))}
        </ul>
    );
};

type SelectOptionItemProps = {
    item: Node<unknown>;
    state: ListState<unknown>;
};

const SelectOptionItem: FC<SelectOptionItemProps> = ({ item, state }) => {
    const ref = useRef<HTMLLIElement>(null);
    const { optionProps, isSelected, isFocused } = useOption({ key: item.key }, state, ref);
    return (
        <li
            {...optionProps}
            ref={ref}
            className={cn(
                "flex w-full cursor-pointer select-none items-center justify-between gap-2",
                "rounded px-2 py-1.5",
                "text-base text-content-primary",
                "focus:outline-none focus:ring-0",
                "aria-disabled:cursor-default aria-disabled:opacity-50",
                isFocused && "bg-surface-hover aria-disabled:bg-transparent",
            )}
        >
            <div className="min-w-0 flex-1">{item.rendered}</div>
            {isSelected && (
                <span className="flex-none">
                    <IconCheckmark2Small className="size-4" />
                </span>
            )}
        </li>
    );
};

// ============================================================================
// Internal Trigger Button
// ============================================================================

type SelectTriggerButtonProps = {
    triggerProps: AriaButtonProps;
    triggerRef: React.RefObject<HTMLButtonElement | null>;
    valueProps: React.HTMLAttributes<HTMLElement>;
    isOpen: boolean;
    displayContent: React.ReactNode;
    loading: boolean;
    disabled: boolean;
    className?: string;
    "data-testid"?: string;
};

const SelectTriggerButton: FC<SelectTriggerButtonProps> = ({
    triggerProps,
    triggerRef,
    valueProps,
    isOpen,
    displayContent,
    loading,
    disabled,
    className,
    "data-testid": dataTestId,
}) => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    const { buttonProps } = useButton(triggerProps as AriaButtonProps<"button">, triggerRef);

    return (
        <button
            {...buttonProps}
            ref={triggerRef as unknown as React.RefObject<HTMLButtonElement>}
            data-testid={dataTestId}
            disabled={disabled || loading}
            aria-expanded={isOpen || undefined}
            className={cn(
                "flex h-9 w-full items-center justify-between gap-2",
                "rounded-lg border border-border-input-default bg-surface-input px-3",
                "text-base text-content-primary",
                "transition-all duration-150 ease-out",
                "outline-none",
                "focus:border-border-input-active focus:ring-1 focus:ring-border-brand focus:ring-offset-1",
                "aria-expanded:border-border-input-active aria-expanded:ring-4 aria-expanded:ring-ring-default aria-expanded:ring-offset-0",
                "disabled:cursor-not-allowed disabled:opacity-50",
                className,
            )}
        >
            <span {...valueProps} className="truncate">
                {displayContent}
            </span>
            {loading ? (
                <IconSpinner className="animate-spin text-content-primary" size="sm" />
            ) : (
                <IconChevronDownSmall size={20} />
            )}
        </button>
    );
};

// ============================================================================
// Builds the rendered content for an item from its declarative children
// ============================================================================

const buildItemContent = (itemChildren: React.ReactNode): React.ReactNode => {
    let icon: React.ReactNode | null = null;
    let description: React.ReactNode | null = null;
    const otherChildren: React.ReactNode[] = [];

    Children.forEach(itemChildren, (child) => {
        if (isValidElement(child)) {
            const dn = getComponentDisplayName(child);
            switch (dn) {
                case SELECT_ITEM_ICON:
                    icon = child;
                    break;
                case SELECT_ITEM_DESCRIPTION:
                    description = child;
                    break;
                default:
                    otherChildren.push(child);
            }
        } else {
            otherChildren.push(child);
        }
    });

    if (icon || description) {
        return (
            <div className="flex min-w-0 items-center gap-2">
                {icon}
                <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate">{otherChildren}</span>
                    {description}
                </div>
            </div>
        );
    }
    return itemChildren;
};

// ============================================================================
// Main Select Component
// ============================================================================

/**
 * A simple select dropdown component built on react-aria.
 *
 * @example
 * ```tsx
 * <Select value={value} onValueChange={setValue}>
 *   <Select.Item value="option1">Option 1</Select.Item>
 *   <Select.Item value="option2">Option 2</Select.Item>
 * </Select>
 * ```
 *
 * @example With icon and description
 * ```tsx
 * <Select value={value} onValueChange={setValue}>
 *   <Select.Item value="folder">
 *     <Select.Item.Icon><IconFolder size={16} /></Select.Item.Icon>
 *     Documents
 *     <Select.Item.Description>Personal files</Select.Item.Description>
 *   </Select.Item>
 * </Select>
 * ```
 */
const Select: FC<React.PropsWithChildren<SelectProps>> & SelectSlots = ({
    children,
    defaultValue,
    value,
    onValueChange,
    disabled = false,
    loading = false,
    placeholder = "Select...",
    formatDisplayValue,
    sameWidth = false,
    name,
    id,
    "aria-label": ariaLabel,
    "data-testid": dataTestId,
    className,
}) => {
    // Extract items and footer from declarative children.
    // SelectItem is never rendered — we extract its props and build <Item> elements
    // for react-stately's collection (see SelectItem comment for why).
    const itemEntries: { value: string; children: React.ReactNode; disabled?: boolean }[] = [];
    let footer: React.ReactElement | null = null;
    Children.forEach(children, (child) => {
        if (isValidElement(child)) {
            const displayName = getComponentDisplayName(child);
            if (displayName === SELECT_ITEM) {
                itemEntries.push({
                    value: child.props.value as string,
                    children: child.props.children as React.ReactNode,
                    disabled: child.props.disabled as boolean | undefined,
                });
            } else if (displayName === SELECT_FOOTER) {
                footer = child;
            }
        }
    });

    const disabledKeys = itemEntries.filter((e) => e.disabled).map((e) => e.value);

    // textValue is required for type-to-select accessibility; fall back to the item's value
    // when children are complex JSX that can't be flattened to a string.
    const collectionChildren = itemEntries.map((entry) => (
        <Item key={entry.value} textValue={labelToTextValue(entry.children) || entry.value}>
            {buildItemContent(entry.children)}
        </Item>
    ));

    const triggerRef = useRef<HTMLButtonElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Track original dialog overflow to restore on close
    const dialogOverflowRef = useRef<string | undefined>(undefined);

    const selectProps = {
        children: collectionChildren,
        selectedKey: value ?? undefined,
        defaultSelectedKey: defaultValue ?? undefined,
        onSelectionChange: (key: React.Key | null) => {
            if (key !== null) {
                onValueChange?.(String(key));
            }
        },
        onOpenChange: (isOpen: boolean) => {
            // HACK: Radix dialog clips the popover due to overflow-x-auto implicitly
            // setting overflow-y: auto. Temporarily override when open. Remove when Radix is replaced.
            const dialog = triggerRef.current?.closest("[role='dialog']") as HTMLElement | null;
            if (dialog) {
                if (isOpen) {
                    dialogOverflowRef.current = dialog.style.overflow;
                    dialog.style.overflow = "visible";
                } else {
                    dialog.style.overflow = dialogOverflowRef.current ?? "";
                    dialogOverflowRef.current = undefined;
                }
            }

            // Without <Overlay>, focus isn't restored to the trigger on close.
            if (!isOpen) {
                triggerRef.current?.focus();
            }
        },
        isDisabled: disabled || loading,
        disabledKeys,
        name,
        id,
        "aria-label": ariaLabel ?? "Select",
    };

    const state = useSelectState(selectProps);

    const { labelProps, triggerProps, valueProps, menuProps } = useSelect(selectProps, state, triggerRef);

    // Determine display content for the trigger
    const selectedItem = state.selectedItem;
    const hasValue = !!selectedItem;

    let displayContent: React.ReactNode;
    if (hasValue) {
        const selectedEntry = itemEntries.find((e) => e.value === String(state.selectedKey));
        const itemLabel = selectedEntry ? extractItemLabel(selectedEntry.children) : selectedItem.rendered;
        displayContent =
            formatDisplayValue && state.selectedKey
                ? formatDisplayValue(String(state.selectedKey), itemLabel)
                : itemLabel;
    } else {
        displayContent =
            typeof placeholder === "string" ? <SelectPlaceholder>{placeholder}</SelectPlaceholder> : placeholder;
    }

    return (
        <div ref={containerRef} className={cn("relative w-fit", className)}>
            <span {...labelProps} className="sr-only">
                {ariaLabel ?? "Select"}
            </span>
            <HiddenSelect
                state={state}
                triggerRef={triggerRef}
                label={ariaLabel ?? "Select"}
                name={name}
                isDisabled={disabled || loading}
            />
            <SelectTriggerButton
                triggerProps={triggerProps}
                triggerRef={triggerRef}
                valueProps={valueProps}
                isOpen={state.isOpen}
                displayContent={displayContent}
                loading={loading}
                disabled={disabled}
                data-testid={dataTestId}
            />
            {state.isOpen && (
                <SelectPopover
                    state={state}
                    triggerRef={triggerRef}
                    placement="bottom start"
                    offset={4}
                    containerPadding={0}
                    triggerWidth={triggerRef.current?.offsetWidth}
                    sameWidth={sameWidth}
                    className={cn(
                        "z-[999]",
                        "max-h-96 overflow-auto",
                        "rounded-lg border border-border-base bg-surface-popover shadow",
                        "p-1",
                    )}
                >
                    <SelectListBox menuProps={menuProps} state={state} />
                    {footer}
                </SelectPopover>
            )}
        </div>
    );
};

/**
 * Declarative item for the Select dropdown.
 *
 * This component is never rendered by React — the parent Select iterates its
 * children, extracts props from each SelectItem, and builds react-stately
 * <Item> elements. This indirection is required because useSelectState's
 * collection system inspects element types at the JSX level: it only
 * recognises <Item> as a collection member, so wrapping Item inside
 * SelectItem would make the collection ignore it.
 */
const SelectItem: FC<SelectItemProps> & SelectItemSlots = ({ children }) => {
    return <>{children}</>;
};
SelectItem.displayName = SELECT_ITEM;
SelectItem.Icon = SelectItemIcon;
SelectItem.Description = SelectItemDescription;

type SelectItemSlots = {
    Icon: typeof SelectItemIcon;
    Description: typeof SelectItemDescription;
};

type SelectSlots = {
    Item: typeof SelectItem;
    Placeholder: typeof SelectPlaceholder;
    Footer: typeof SelectFooter;
};

Select.Item = SelectItem;
Select.Placeholder = SelectPlaceholder;
Select.Footer = SelectFooter;

export { Select };
